import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Bell, Globe, LogOut } from "lucide-react";
import { Button } from "@ds/ui/button";
import { Row, RowGroup, RowIcon, SectionHeader } from "@ds/patterns/row";
import { env } from "@/app/config/env";
import { announce } from "@/app/lib/announce";
import { isNative, openAppSettings, platform } from "@/app/platform";
import { usePushRegistration } from "@/features/devices";
import { useAuthStatus, useSignOut } from "@/shared/auth";

export const Route = createFileRoute("/_app/settings")({
  component: SettingsScreen,
});

function SettingsScreen() {
  const { t, i18n } = useTranslation(["common", "devices"]);
  const { user } = useAuthStatus();
  const signOut = useSignOut();
  const { state, enable } = usePushRegistration();

  const pushState = isNative ? state : "unsupported";

  /**
   * The row has said "turn them on in iOS Settings" since it was written, and until now
   * nothing happened when you tapped it — the sentence named a destination the app had no
   * way to reach. This is that way.
   */
  async function openSettings() {
    if (await openAppSettings()) return;
    announce("common:permission.openSettingsFailed", { severity: "warning" });
  }

  return (
    <div className="flex flex-col gap-7">
      <h1 className="text-title">{t("common:nav.settings")}</h1>

      <RowGroup>
        <Row title={user?.name ?? user?.email ?? ""} description={user?.email} />
      </RowGroup>

      <div className="flex flex-col gap-2">
        <SectionHeader title={t("devices:push.title")} />
        <RowGroup>
          <Row
            leading={<RowIcon><Bell /></RowIcon>}
            title={t(`devices:push.state.${pushState}`)}
            description={t("devices:push.body")}
            /**
             * Three tap behaviours, because three states need three different things.
             * "failed" is retryable in place — permission was granted and the machinery
             * missed. "blocked" cannot be retried at all: iOS never prompts twice, so the
             * only move is the app's own page in Settings. "on" and "unsupported" have
             * nothing to offer and stay untappable rather than pretending.
             */
            onSelect={
              pushState === "idle" || pushState === "failed"
                ? () => void enable()
                : pushState === "blocked"
                  ? () => void openSettings()
                  : undefined
            }
          />
        </RowGroup>
      </div>

      <div className="flex flex-col gap-2">
        <SectionHeader title={t("devices:language")} />
        <RowGroup>
          {env.locales.map((locale) => (
            <Row
              key={locale}
              leading={<RowIcon><Globe /></RowIcon>}
              title={locale.toUpperCase()}
              trailing={
                i18n.resolvedLanguage === locale ? (
                  <span className="text-support text-accent">✓</span>
                ) : undefined
              }
              onSelect={() => void i18n.changeLanguage(locale)}
            />
          ))}
        </RowGroup>
      </div>

      <Button
        variant="secondary"
        /**
         * NO `navigate` HERE, AND THAT IS THE FIX RATHER THAN AN OMISSION.
         *
         * It used to sign out and then push to /sign-in itself. That call runs the instant
         * the mutation resolves — BEFORE React has re-rendered with the new session, so the
         * router's auth context still said "authenticated". The sign-in route's own guard
         * therefore bounced straight back into the app, and the person who pressed Sign out
         * stayed exactly where they were, still signed in on screen.
         *
         * The route guards already own this: when the session becomes null the status turns
         * "anonymous", the router is invalidated, and `_app` redirects. Letting the one
         * mechanism do it removes the race instead of re-running it faster.
         */
        onClick={() => void signOut.mutateAsync()}
      >
        <LogOut />
        {t("common:actions.signOut")}
      </Button>

      {/*
        WHICH BUILD AM I LOOKING AT. Three identities that look identical is a long
        afternoon: a bug reproduced against the wrong database, a "fixed" thing that was
        never deployed. The service host is the half that actually settles it, and it shows
        on every identity except production — where there is only one answer and printing it
        would just be noise on a user's screen.
      */}
      <p className="text-legal text-muted-foreground text-center tabular-nums">
        {env.appName} {env.version} · {env.appEnv} · {platform}
        {env.showsBuildDetail ? ` · ${new URL(env.apiUrl).host}` : ""}
      </p>
    </div>
  );
}
